import type { paths } from "@securityjourney/svc-scorm/dist/gen/token";
import type {
  paths as scormPaths,
  components as scormComponents,
} from "@securityjourney/svc-scorm/dist/gen/scorm";

import { OpenApiFetchWrapper, type ServiceResponse } from "./openApiFetchWrapper";

export type AccessTokenResponse = {
  /** Can be passed in the Authorization header to services that support SCORM auth. */
  access_token: string;
  /** Expiry duration in seconds */
  expires_in: number;
  /** The semi-stable SCORM identity */
  sub: string;
};

export type Course = scormComponents["schemas"]["securityjourney.proto.scorm.Course"];
export type GeneratePackageRequest =
  scormComponents["schemas"]["securityjourney.proto.scorm.GeneratePackageRequest"];
export type RecordLessonAttemptRequest =
  scormComponents["schemas"]["securityjourney.proto.scorm.RecordLessonAttemptRequest"];

/**
 * Handles the SCORM token exchange endpoint.
 * Uses no standard bearer auth — the SCORM magic token itself bootstraps auth.
 */
export class ScormTokenClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    super({ noAuth: true });
  }

  /**
   * Exchange a SCORM magic token for an access token scoped to a specific
   * learner identity and lesson.
   *
   * @param scorm_token   The "magic" SCORM key passed in by our package.
   * @param scorm_identity The value of GetStudentID() from the SCORM runtime.
   * @param lesson_key    The key of the lesson being accessed.
   */
  async getScormToken(
    scorm_token: string,
    scorm_identity: string,
    lesson_key: string
  ): Promise<ServiceResponse<AccessTokenResponse>> {
    const { data, error } = await this.post("/svc/scorm/auth/token", {
      body: {
        subject_token: scorm_token,
        scope: [`identity-${scorm_identity}`, `lesson-${lesson_key}`].join(" "),
        subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
      },
    });

    if (error) {
      return { data: null, error };
    }

    const { access_token, expires_in } = data;
    const sub: string = JSON.parse(atob(access_token.split(".")[1])).sub;
    return { data: { access_token, expires_in: Number(expires_in), sub }, error: null };
  }
}

/**
 * Handles SCORM package generation.
 * Uses standard bearer auth.
 */
export class ScormPackageClient extends OpenApiFetchWrapper<scormPaths> {
  constructor() {
    super();
  }

  /**
   * Generate and download a SCORM package as a zip file.
   *
   * @param request - The idp_uuid, courses, and tenant for the package.
   */
  async generateScormPackage(request: GeneratePackageRequest): Promise<ServiceResponse<void>> {
    const result = await this.post("/svc/scorm/package", { body: request });

    if (result.error) {
      return result;
    }

    const zipPackage = result.data?.zipPackage;
    if (!zipPackage) {
      return { data: null, error: new Error("Invalid response: zipPackage is missing.") };
    }

    // Convert base64 payload to a Blob and trigger a browser download
    const binary = atob(zipPackage);
    const array = Uint8Array.from(binary, c => c.charCodeAt(0));
    const blob = new Blob([array], { type: "application/zip" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sj-scorm-package.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { data: undefined as unknown as void, error: null };
  }

  /**
   * Record that a SCORM lesson attempt has started (i.e. the lesson was launched).
   *
   * @param request - The lessonKey and tenant for the attempt.
   */
  async recordLessonAttemptStarted(request: RecordLessonAttemptRequest) {
    return this.post("/svc/scorm/lesson-attempt/started", { body: request });
  }

  /**
   * Record that a SCORM lesson attempt has been completed (i.e. the learner finished the lesson).
   *
   * @param request - The lessonKey and tenant for the attempt.
   */
  async recordLessonAttemptCompleted(request: RecordLessonAttemptRequest) {
    return this.post("/svc/scorm/lesson-attempt/completed", { body: request });
  }

  /**
   * Export all SCORM lesson attempts for a tenant as a CSV file. Returns the CSV content as a string.
   *
   * @param tenant - The tenant slug (e.g. "securityjourney").
   */
  async exportLessonAttempts(tenant: string) {
    return await this.get("/svc/scorm/{tenant}/lesson-attempts/export", {
      params: { path: { tenant } },
      parseAs: "text",
    });
  }
}

let scormTokenClientInstance: ScormTokenClient | null = null;
let scormPackageClientInstance: ScormPackageClient | null = null;

export const getScormTokenClient = (): ScormTokenClient => {
  if (!scormTokenClientInstance) {
    scormTokenClientInstance = new ScormTokenClient();
  }
  return scormTokenClientInstance;
};

export const getScormPackageClient = (): ScormPackageClient => {
  if (!scormPackageClientInstance) {
    scormPackageClientInstance = new ScormPackageClient();
  }
  return scormPackageClientInstance;
};
